import { useCallback, useEffect, useMemo, useState } from "react";

export const DEFAULT_STATE = {
  searchValue: "",
  searchedColumn: "",
  pagination: {
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
  },
  sorting: {
    field: "",
    order: "",
  },
  params: {},
};

export const loadFromSession = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const saveToSession = (key, value) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const resetSession = (key, defaultState = DEFAULT_STATE) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(defaultState));
  } catch {
    // noop
  }
};

export const removeSession = (key) => {
  sessionStorage.removeItem(key);
};

const useFilters = (
  storageKey,
  defaultState = DEFAULT_STATE
) => {
  const [filters, setFilters] = useState(() =>
    loadFromSession(storageKey, defaultState)
  );

  // persistencia
  useEffect(() => {
    saveToSession(storageKey, filters);
  }, [storageKey, filters]);

  // Table change
  const onTableChange = useCallback((pagination, _, sorting) => {
    setFilters(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        ...pagination,
      },
      sorting,
    }));
  }, []);

  // Search
  const onSearch = useCallback((value, column) => {
    setFilters(prev => ({
      ...prev,
      searchValue: value,
      searchedColumn: column,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
      params: {
        ...prev.params,
        [column]: value,
      }
    }));
  }, []);

  const setParams = useCallback((updater) => {
    setFilters(prev => ({
      ...prev,
      params: typeof updater === "function"
        ? updater(prev.params)
        : updater,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  }, []);

  // Reset total
  const resetFilters = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setFilters(defaultState);
  }, [defaultState, storageKey]);

  const hasActiveFilters = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(defaultState);
  }, [filters, defaultState]);

  return {
    filters,
    setParams,  // opcional, por si necesitás algo custom
    onTableChange,
    onSearch,
    resetFilters,
    resetPagination,
    hasActiveFilters,
  };
};

export default useFilters;